import Select from 'antd/lib/select'
import { useMemo } from 'react'
import { AudioOutlined, DesktopOutlined, VideoCameraOutlined } from '@ant-design/icons'
import styles from './ProctoringPermissionsGate.module.less'
import { PermissionKind, PermissionStatus } from 'Components/Proctoring/proctoring.types'
import type { PermissionItemProps } from 'Components/Proctoring/proctoring.types'

const ICONS = {
    [PermissionKind.Webcam]: VideoCameraOutlined,
    [PermissionKind.Microphone]: AudioOutlined,
    [PermissionKind.Screen]: DesktopOutlined,
}

export const PermissionItem = ({
    label,
    kind,
    status = PermissionStatus.Prompt,
    availableDevices = [],
    hideAvailableDevices = false,
    selectedDeviceId,
    onDeviceChange,
}: PermissionItemProps) => {
    const Icon = useMemo(() => ICONS[kind] || DesktopOutlined, [kind])
    const isGranted = status === PermissionStatus.Granted

    const statusClass = useMemo(() => {
        if (status === PermissionStatus.Granted) return styles.permissionGranted
        if (status === PermissionStatus.Denied) return styles.permissionDenied
        return styles.permissionPrompt
    }, [status])

    const deviceOptions = useMemo(
        () =>
            (availableDevices || []).map(device => ({
                label: device.label || `Device ${device.deviceId?.slice(0, 8) || ''}`,
                value: device.deviceId,
            })),
        [availableDevices]
    )

    return (
        <div className={styles.permissionItem} data-testid={`permission-item-${kind}`}>
            <div className={styles.permissionItemHeader}>
                <div className={`${styles.permissionIconWrap} ${statusClass}`}>
                    <Icon className={styles.permissionIcon} />
                </div>
                <p className={styles.permissionLabel}>{label}</p>
            </div>
            {!hideAvailableDevices ? (
                <Select
                    className={styles.deviceSelect}
                    popupClassName={styles.deviceSelectDropdown}
                    value={selectedDeviceId || undefined}
                    onChange={value => onDeviceChange?.(value)}
                    placeholder={!isGranted ? 'Permission Required' : 'Select'}
                    disabled={!isGranted}
                    allowClear={false}
                    size="large"
                    options={deviceOptions}
                    data-testid={`permission-device-select-${kind}`}
                />
            ) : null}
        </div>
    )
}
